import { useState } from 'react';
import type { LienFormulaire } from '@rh/shared';
import { appel } from '../api';

/**
 * Lien de formulaire d'une annonce (docs/FORMULAIRE_PUBLIC.md § 8).
 *
 * Trois gestes, dans cet ordre d'importance : **copier** le lien pour le coller
 * dans une publication, le **partager** avec le partage natif du téléphone, et
 * afficher son **QR code** pour une affiche en boutique. Désactiver et
 * régénérer sont en retrait : ce sont des gestes rares, et régénérer casse tous
 * les liens déjà collés.
 */
export function CarteLienFormulaire({
  lien,
  rh,
  surChangement,
}: {
  lien: LienFormulaire;
  rh: boolean;
  surChangement: (lien: LienFormulaire) => void;
}) {
  const [copie, setCopie] = useState<'formulaire' | 'whatsapp' | null>(null);
  const [qrOuvert, setQrOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function copier(texte: string, lequel: 'formulaire' | 'whatsapp'): Promise<void> {
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(lequel);
      setTimeout(() => setCopie(null), 2500);
    } catch {
      // Presse-papiers refusé (contexte non sécurisé, permission) : le lien
      // reste sélectionnable à la main, on ne bloque rien.
      setErreur('Copie impossible : sélectionnez le lien à la main.');
    }
  }

  async function partager(texte: string): Promise<void> {
    if (!navigator.share) return copier(texte, 'formulaire');
    try {
      await navigator.share({ url: texte });
    } catch {
      // L'utilisateur a fermé la feuille de partage : ce n'est pas une erreur.
    }
  }

  async function agir(chemin: string, method: 'POST' | 'PATCH', corps?: unknown): Promise<void> {
    setEnCours(true);
    setErreur(null);
    try {
      surChangement(
        await appel<LienFormulaire>(chemin, {
          method,
          ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
        }),
      );
      setQrOuvert(false);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Action impossible');
    } finally {
      setEnCours(false);
    }
  }

  if (!lien.actif) {
    return (
      <p className="lien-desactive">
        Lien désactivé{lien.desactiveLe ? ` le ${new Date(lien.desactiveLe).toLocaleDateString('fr-FR')}` : ''} ·{' '}
        {lien.compteurs.recues} reçues
      </p>
    );
  }

  return (
    <div className="carte-lien-formulaire">
      <p className="url-lien">
        <a href={lien.url} target="_blank" rel="noreferrer noopener">
          {lien.url}
        </a>
      </p>

      <div className="actions">
        <button type="button" className="bouton" onClick={() => void copier(lien.url, 'formulaire')}>
          {copie === 'formulaire' ? 'Copié' : 'Copier'}
        </button>
        <button type="button" className="bouton-second" onClick={() => void partager(lien.url)}>
          Partager
        </button>
        <button type="button" className="bouton-second" onClick={() => setQrOuvert((v) => !v)}>
          {qrOuvert ? 'Masquer le QR code' : 'QR code'}
        </button>
      </div>

      {qrOuvert && (
        <div className="qr-lien">
          <img src={`/api/v1/liens-formulaire/${lien.id}/qr.png`} alt={`QR code du lien ${lien.url}`} />
          <a className="lien" href={`/api/v1/liens-formulaire/${lien.id}/qr.png`} download="formulaire.png">
            Télécharger l’image
          </a>
        </div>
      )}

      <p className="compteurs-lien">
        <strong>{lien.compteurs.recues}</strong> reçues · <strong>{lien.compteurs.doublons}</strong>{' '}
        déjà candidats · <strong>{lien.compteurs.rejetees}</strong> rejetées
      </p>

      {lien.urlWhatsapp && (
        <div className="lien-whatsapp">
          <p>
            Lien « Postuler par WhatsApp », à coller à côté du premier :
            <br />
            <a href={lien.urlWhatsapp} target="_blank" rel="noreferrer noopener">
              ouvrir
            </a>
          </p>
          <button
            type="button"
            className="bouton-second"
            onClick={() => void copier(lien.urlWhatsapp!, 'whatsapp')}
          >
            {copie === 'whatsapp' ? 'Copié' : 'Copier le lien WhatsApp'}
          </button>
        </div>
      )}

      {erreur && <p className="erreur">{erreur}</p>}

      {rh && (
        <div className="actions actions-rares">
          <button
            type="button"
            className="lien"
            disabled={enCours}
            onClick={() => void agir(`/liens-formulaire/${lien.id}`, 'PATCH', { actif: false })}
          >
            Désactiver
          </button>
          <button
            type="button"
            className="lien"
            disabled={enCours}
            onClick={() => {
              if (
                !window.confirm(
                  'Régénérer le lien ? L’ancien cessera de fonctionner : tout ce qui a déjà été publié ou imprimé ne mènera plus nulle part.',
                )
              ) {
                return;
              }
              void agir(`/liens-formulaire/${lien.id}/regenerer`, 'POST');
            }}
          >
            Régénérer
          </button>
        </div>
      )}
    </div>
  );
}
